import { HttpClient } from '@angular/common/http';

import { Observable, ReplaySubject } from 'rxjs';
import { Injectable } from '@angular/core';
import { StreamPlayFilters, StreamPlayState } from './types/stream-play-models.type';
import { StreamPlayStoreUtils } from './utils/stream-play-store-utils';
import { StreamPlayResponse } from './types/stream-play-api.type';
import { StreamPlayUtils } from './utils/stream-play-utils';
import { StreamPlayModule } from './stream-play.module';

@Injectable({providedIn: StreamPlayModule})
export class StreamPlayService {
  private cache: { [url: string]: ReplaySubject<StreamPlayState> };

  constructor(private httpClient: HttpClient) {
    this.cache = {};
  }

  /**
   * Triggers an API call to StreamPlay while handling the caching and the state.
   * @param apiUrl Stream play URL.
   * @param pageSize? The page size.
   * @param streamPlayState? The existing player state.
   * @param streamPlayFilters? The query parameter filters.
   * @param appendData? A flag to overwrite or append data from the state.
   * @param streamPlayFiltersBody? The body parameter filters.
   * @param noCache? always execute ajax call.
   *
   * @see StreamPlayState
   * @see StreamPlayFilters
   */
  public triggerApiCall(
      apiUrl: string | undefined,
      pageSize?: number | undefined,
      streamPlayState?: StreamPlayState | undefined | null,
      streamPlayFilters?: StreamPlayFilters | undefined,
      appendData?: boolean,
      streamPlayFiltersBody?: StreamPlayFilters | undefined,
      noCache?: boolean | undefined,
  ): Observable<StreamPlayState> {
    let streamPlayUrl = '';
    let noMoreData = false;

    pageSize = pageSize || 10;

    streamPlayFilters = streamPlayFilters || {};
    const pagingData = StreamPlayStoreUtils.getPagingDataFromStreamPlayState(streamPlayState);

    if (pagingData) {
      streamPlayUrl = StreamPlayUtils.generateStreamPlayUrl(apiUrl, pagingData, streamPlayFilters);
      if (!StreamPlayUtils.isMoreDataAvailable(pagingData)) {
        streamPlayState = StreamPlayStoreUtils.setStreamPlayStateNoMoreData(streamPlayState);
        noMoreData = true;
      }
    } else {
      streamPlayUrl = StreamPlayUtils.generateStreamPlayUrl(apiUrl, null, streamPlayFilters, pageSize);
    }

    const cacheKey = streamPlayUrl + JSON.stringify(streamPlayFiltersBody);

    if (noCache || !this.cache[cacheKey]) {

      if (noMoreData) {
        // no more data available
        this.cache[streamPlayUrl].next(streamPlayState);

      } else {

        this.cache[cacheKey] = new ReplaySubject<StreamPlayState>(1);


        let ajaxCall: Observable<StreamPlayResponse>;

        if (streamPlayFiltersBody) {
          ajaxCall = this.httpClient.post<StreamPlayResponse>(streamPlayUrl, streamPlayFiltersBody);
        } else {

          ajaxCall = this.httpClient.get<StreamPlayResponse>(streamPlayUrl);
        }


        ajaxCall.subscribe(response => {
              if (response) {
                streamPlayState = StreamPlayStoreUtils.setStreamPlayStateAndAppendData(
                    streamPlayState,
                    response,
                    appendData,
                );

                this.cache[cacheKey].next(streamPlayState);

              } else {

                this.cache[cacheKey].next({
                  data: [],
                });
              }
            },
            error => {

              this.cache[cacheKey].next({error: true});
            },
        );
      }
    }

    return this.cache[cacheKey].asObservable();
  }
}
